import * as React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Fleets } from '@app/Fleets/Fleets';
import { Devices } from '@app/Devices/Devices';
import { Repositories } from '@app/Repositories/Repositories';
import { CreateRepository } from '@app/CreateRepository/CreateRepository';
import { EditRepository } from '@app/EditRepository/EditRepository';
import { ImageBuilds } from '@app/ImageBuilds/ImageBuilds';
import { CreateImageBuild } from '@app/CreateImageBuild/CreateImageBuild';
import { CreateImageExport } from '@app/CreateImageExport/CreateImageExport';
import { ViewImageBuild } from '@app/ViewImageBuild/ViewImageBuild';
import { Settings } from '@app/Settings/Settings';
import { ACMOpenShift } from '@app/ACMOpenShift/ACMOpenShift';
import { ACMOpenShiftOverview } from '@app/ACMOpenShift/ACMOpenShiftOverview';
import { SoftwareCatalog } from '@app/SoftwareCatalog/SoftwareCatalog';
import { EmptyPage } from '@app/EmptyPage/EmptyPage';
import { NotFound } from '@app/NotFound/NotFound';

export interface IAppRoute {
  label?: string; // Excluding the label will exclude the route from the nav sidebar in AppLayout
  /* eslint-disable @typescript-eslint/no-explicit-any */
  element: React.ReactElement;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  isDisabled?: boolean;
  exact?: boolean;
  path: string;
  title: string;
  routes?: undefined;
}

export interface IAppRouteGroup {
  label: string;
  routes: IAppRoute[];
}

export type AppRouteConfig = IAppRoute | IAppRouteGroup;

// Edge Management routes (original navigation)
const edgeManagerRoutes: AppRouteConfig[] = [
  {
    element: <ACMOpenShiftOverview />,
    exact: true,
    label: 'Overview',
    path: '/overview',
    title: 'Overview',
  },
  {
    element: <Devices />,
    exact: true,
    label: 'Devices',
    path: '/devices',
    title: 'Devices',
  },
  {
    element: <Fleets />,
    exact: true,
    label: 'Fleets',
    path: '/groups',
    title: 'Fleets',
  },
  {
    element: <SoftwareCatalog />,
    exact: true,
    label: 'Software catalog',
    path: '/software-catalog',
    title: 'Software catalog',
  },
  {
    element: <ImageBuilds />,
    exact: true,
    label: 'Image builds',
    path: '/image-builds',
    title: 'Image builds',
  },
  {
    element: <CreateImageBuild />,
    exact: true,
    path: '/image-builds/create',
    title: 'Build an image',
  },
  {
    element: <CreateImageExport />,
    exact: true,
    path: '/image-builds/create-export',
    title: 'Export image',
  },
  {
    element: <ViewImageBuild />,
    exact: true,
    path: '/image-builds/:id',
    title: 'Image build details',
  },
  {
    element: <Repositories />,
    exact: true,
    path: '/repositories',
    title: 'Repositories',
  },
  {
    element: <CreateRepository />,
    exact: true,
    path: '/repositories/create',
    title: 'Create Repository',
  },
  {
    element: <EditRepository />,
    exact: true,
    path: '/repositories/edit/:id',
    title: 'Edit Repository',
  },
  {
    element: <Settings />,
    exact: true,
    path: '/authentications',
    title: 'Authentications',
  },
  {
    label: 'User management',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Identities',
        path: '/user-management/identities',
        title: 'Identities',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Roles',
        path: '/user-management/roles',
        title: 'Roles',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Identity providers',
        path: '/user-management/identity-providers',
        title: 'Identity providers',
      },
    ],
  },
];

// ACM OpenShift routes (new navigation structure)
const acmOpenShiftRoutes: AppRouteConfig[] = [
  {
    label: 'Home',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Welcome',
        path: '/acm-openshift/home/welcome',
        title: 'Welcome',
      },
      {
        element: <Navigate to="/acm-openshift/edge-management/overview" replace />,
        exact: true,
        label: 'Overview',
        path: '/acm-openshift/home/overview',
        title: 'Overview',
      },
    ],
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Search',
    path: '/acm-openshift/search',
    title: 'Search',
  },
  {
    label: 'Infrastructure',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Clusters',
        path: '/acm-openshift/infrastructure/clusters',
        title: 'Clusters',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Automation',
        path: '/acm-openshift/infrastructure/automation',
        title: 'Automation',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Host inventory',
        path: '/acm-openshift/infrastructure/host-inventory',
        title: 'Host inventory',
      },
    ],
  },
  {
    label: 'Edge Management',
    routes: [
      {
        element: <ACMOpenShiftOverview />,
        exact: true,
        label: 'Overview',
        path: '/acm-openshift/edge-management/overview',
        title: 'Overview',
      },
      {
        element: <Devices />,
        exact: true,
        label: 'Devices',
        path: '/acm-openshift/edge-management/devices',
        title: 'Devices',
      },
      {
        element: <Fleets />,
        exact: true,
        label: 'Fleets',
        path: '/acm-openshift/edge-management/groups',
        title: 'Fleets',
      },
      {
        element: <SoftwareCatalog />,
        exact: true,
        label: 'Software catalog',
        path: '/acm-openshift/edge-management/software-catalog',
        title: 'Software catalog',
      },
      {
        element: <ImageBuilds />,
        exact: true,
        label: 'Image builds',
        path: '/acm-openshift/edge-management/image-builds',
        title: 'Image builds',
      },
      {
        element: <CreateImageBuild />,
        exact: true,
        path: '/acm-openshift/edge-management/image-builds/create',
        title: 'Build an image',
      },
      {
        element: <CreateImageExport />,
        exact: true,
        path: '/acm-openshift/edge-management/image-builds/create-export',
        title: 'Export image',
      },
      {
        element: <ViewImageBuild />,
        exact: true,
        path: '/acm-openshift/edge-management/image-builds/:id',
        title: 'Image build details',
      },
      {
        element: <Repositories />,
        exact: true,
        path: '/acm-openshift/edge-management/repositories',
        title: 'Repositories',
      },
      {
        element: <CreateRepository />,
        exact: true,
        path: '/acm-openshift/edge-management/repositories/create',
        title: 'Create Repository',
      },
      {
        element: <EditRepository />,
        exact: true,
        path: '/acm-openshift/edge-management/repositories/edit/:id',
        title: 'Edit Repository',
      },
      {
        element: <Settings />,
        exact: true,
        path: '/acm-openshift/edge-management/authentications',
        title: 'Authentications',
      },
    ],
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Applications',
    path: '/acm-openshift/applications',
    title: 'Applications',
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Governance',
    path: '/acm-openshift/governance',
    title: 'Governance',
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Credentials',
    path: '/acm-openshift/credentials',
    title: 'Credentials',
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Observe',
    path: '/acm-openshift/observe',
    title: 'Observe',
  },
  {
    label: 'User management',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Identities',
        path: '/acm-openshift/user-management/identities',
        title: 'Identities',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Roles',
        path: '/acm-openshift/user-management/roles',
        title: 'Roles',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Identity providers',
        path: '/acm-openshift/user-management/identity-providers',
        title: 'Identity providers',
      },
    ],
  },
];

// Ansible Automation Platform navigation
const ansibleRoutes: AppRouteConfig[] = [
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Home',
    path: '/ansible/home',
    title: 'Home',
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Projects',
    path: '/ansible/projects',
    title: 'Projects',
  },
  {
    label: 'Automation Execution',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Templates',
        path: '/ansible/automation-execution/templates',
        title: 'Templates',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Jobs',
        path: '/ansible/automation-execution/jobs',
        title: 'Jobs',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Schedules',
        path: '/ansible/automation-execution/schedules',
        title: 'Schedules',
      },
    ],
  },
  {
    label: 'Automation Infrastructure',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Inventories',
        path: '/ansible/automation-infrastructure/inventories',
        title: 'Inventories',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Hosts',
        path: '/ansible/automation-infrastructure/hosts',
        title: 'Hosts',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Credentials',
        path: '/ansible/automation-infrastructure/credentials',
        title: 'Credentials',
      },
    ],
  },
  {
    label: 'Ansible Lightspeed with IBM watsonx Code Assistant',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Suggestions',
        path: '/ansible/lightspeed/suggestions',
        title: 'Suggestions',
      },
    ],
  },
  {
    label: 'Edge Management',
    routes: [
      {
        element: <ACMOpenShiftOverview />,
        exact: true,
        label: 'Overview',
        path: '/ansible/edge-management/overview',
        title: 'Overview',
      },
      {
        element: <Devices />,
        exact: true,
        label: 'Devices',
        path: '/ansible/edge-management/devices',
        title: 'Devices',
      },
      {
        element: <Fleets />,
        exact: true,
        label: 'Fleets',
        path: '/ansible/edge-management/groups',
        title: 'Fleets',
      },
      {
        element: <SoftwareCatalog />,
        exact: true,
        label: 'Software catalog',
        path: '/ansible/edge-management/software-catalog',
        title: 'Software catalog',
      },
      {
        element: <ImageBuilds />,
        exact: true,
        label: 'Image builds',
        path: '/ansible/edge-management/image-builds',
        title: 'Image builds',
      },
      {
        element: <Repositories />,
        exact: true,
        path: '/ansible/edge-management/repositories',
        title: 'Repositories',
      },
      {
        element: <CreateRepository />,
        exact: true,
        path: '/ansible/edge-management/repositories/create',
        title: 'Create Repository',
      },
      {
        element: <EditRepository />,
        exact: true,
        path: '/ansible/edge-management/repositories/edit/:id',
        title: 'Edit Repository',
      },
      {
        element: <Settings />,
        exact: true,
        path: '/ansible/edge-management/authentications',
        title: 'Authentications',
      },
      {
        element: <CreateImageBuild />,
        exact: true,
        path: '/ansible/edge-management/image-builds/create',
        title: 'Build an image',
      },
      {
        element: <CreateImageExport />,
        exact: true,
        path: '/ansible/edge-management/image-builds/create-export',
        title: 'Export image',
      },
      {
        element: <ViewImageBuild />,
        exact: true,
        path: '/ansible/edge-management/image-builds/:id',
        title: 'Image build details',
      },
    ],
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Quick Starts',
    path: '/ansible/quick-starts',
    title: 'Quick Starts',
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'Search',
    path: '/ansible/search',
    title: 'Search',
  },
  {
    label: 'Utilities',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Topology view',
        path: '/ansible/utilities/topology',
        title: 'Topology view',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Notifications',
        path: '/ansible/utilities/notifications',
        title: 'Notifications',
      },
    ],
  },
  {
    label: 'Access',
    routes: [
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Users',
        path: '/ansible/access/users',
        title: 'Users',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Teams',
        path: '/ansible/access/teams',
        title: 'Teams',
      },
      {
        element: <EmptyPage />,
        exact: true,
        label: 'Roles',
        path: '/ansible/access/roles',
        title: 'Roles',
      },
    ],
  },
  {
    element: <EmptyPage />,
    exact: true,
    label: 'User Interface Settings',
    path: '/ansible/user-interface-settings',
    title: 'User Interface Settings',
  },
];

// Red Hat Manager 1.1 routes
const redHatManagerRoutes: AppRouteConfig[] = [
  {
    element: <ACMOpenShiftOverview />,
    exact: true,
    label: 'Overview',
    path: '/red-hat-manager/overview',
    title: 'Overview',
  },
  {
    element: <Devices />,
    exact: true,
    label: 'Devices',
    path: '/red-hat-manager/devices',
    title: 'Devices',
  },
  {
    element: <Fleets />,
    exact: true,
    label: 'Fleets',
    path: '/red-hat-manager/groups',
    title: 'Fleets',
  },
  {
    element: <SoftwareCatalog />,
    exact: true,
    label: 'Software catalog',
    path: '/red-hat-manager/software-catalog',
    title: 'Software catalog',
  },
  {
    element: <ImageBuilds />,
    exact: true,
    label: 'Image builds',
    path: '/red-hat-manager/images',
    title: 'Image builds',
  },
  {
    element: <CreateImageBuild />,
    exact: true,
    path: '/red-hat-manager/images/create',
    title: 'Build an image',
  },
  {
    element: <CreateImageExport />,
    exact: true,
    path: '/red-hat-manager/images/create-export',
    title: 'Export image',
  },
  {
    element: <ViewImageBuild />,
    exact: true,
    path: '/red-hat-manager/images/:id',
    title: 'Image build details',
  },
  {
    element: <Repositories />,
    exact: true,
    path: '/red-hat-manager/repositories',
    title: 'Repositories',
  },
  {
    element: <CreateRepository />,
    exact: true,
    path: '/red-hat-manager/repositories/create',
    title: 'Create Repository',
  },
  {
    element: <EditRepository />,
    exact: true,
    path: '/red-hat-manager/repositories/edit/:id',
    title: 'Edit Repository',
  },
  {
    element: <Settings />,
    exact: true,
    path: '/red-hat-manager/authentications',
    title: 'Authentications',
  },
];

// Combined routes for routing
const routes: AppRouteConfig[] = [
  ...edgeManagerRoutes,
  {
    element: <ACMOpenShift />,
    exact: true,
    path: '/acm-openshift',
    title: 'ACM | OpenShift',
  },
  ...acmOpenShiftRoutes,
  {
    element: <Navigate to="/ansible/edge-management/overview" replace />,
    exact: true,
    path: '/ansible',
    title: 'Ansible Automation',
  },
  ...ansibleRoutes,
  {
    element: <Navigate to="/red-hat-manager/overview" replace />,
    exact: true,
    path: '/red-hat-manager',
    title: 'Edge Management',
  },
  ...redHatManagerRoutes,
  {
    element: <EditRepository />,
    exact: true,
    path: '/repositories/edit/:id',
    title: 'Edit Repository',
  },
  {
    element: <EditRepository />,
    exact: true,
    path: '/red-hat-manager/repositories/edit/:id',
    title: 'Edit Repository',
  },
];

const flattenedRoutes: IAppRoute[] = routes.reduce(
  (flattened, route) => [...flattened, ...(route.routes ? route.routes : [route])],
  [] as IAppRoute[],
);

// Redirect /settings to Red Hat Manager Authentications so the left nav is consistent (Overview, Devices, Fleets, etc.)
const SettingsRedirect: React.FunctionComponent = () => (
  <Navigate to="/red-hat-manager/authentications" replace />
);

const AppRoutes = (): React.ReactElement => (
  <Routes>
    <Route path="/" element={<Navigate to="/red-hat-manager/overview" replace />} />
    <Route path="/settings" element={<SettingsRedirect />} />
    {flattenedRoutes.map(({ path, element }) => (
      <Route key={path} path={path} element={element} />
    ))}
    <Route element={<NotFound />} />
  </Routes>
);

export { AppRoutes, routes, edgeManagerRoutes, acmOpenShiftRoutes, ansibleRoutes, redHatManagerRoutes };
